import React, { useContext, useState, useMemo } from 'react';
import { CourseDesignContext } from '../context/CourseDesignContext';
import { CourseIDContext } from '../context/CourseIDContext';
import QuestionLogicRepresentation from '../molecules/QuestionLogicRepresentation';
import PromptModal from '../components/PromptModal';
import MessageModal from '../components/MessageModal';
import DuplicateResolutionModal from '../components/DuplicateResolutionModal';
import axios from 'axios';

const SharingMaster = () => {
  const { courseDesignState } = useContext(CourseDesignContext);
  const { courseID } = useContext(CourseIDContext);
  const [selectedTopicDataID, setSelectedTopicDataID] = useState(null);
  const [selectedQuestions, setSelectedQuestions] = useState(new Set());
  const [isSharing, setIsSharing] = useState(false);
  const [showPromptModal, setShowPromptModal] = useState(false);
  const [defaultModuleName, setDefaultModuleName] = useState('');
  const [showMessageModal, setShowMessageModal] = useState(false);
  const [messageModalConfig, setMessageModalConfig] = useState({
    title: '',
    message: '',
    type: 'info'
  });
  const [showDuplicateModal, setShowDuplicateModal] = useState(false);
  const [duplicateInfo, setDuplicateInfo] = useState({
    questionModule: '',
    existingCount: 0,
    questionsToShare: [],
    courseTitle: '',
    instructorName: ''
  });

  const apiUrl = process.env.REACT_APP_API_BASE_URL;

  // Get all topics that have questions associated with them
  const topicsWithQuestions = useMemo(() => {
    const questionsGroupedByTopic = {};
    
    // Group questions by topic
    courseDesignState
      .filter(item => item.dataType === 'question')
      .forEach(question => {
        const topicID = question.questionCourseDate;
        if (!questionsGroupedByTopic[topicID]) {
          questionsGroupedByTopic[topicID] = [];
        }
        questionsGroupedByTopic[topicID].push(question);
      });

    // Get topic details and include question count
    return Object.keys(questionsGroupedByTopic)
      .map(topicID => {
        const topicData = courseDesignState.find(item => 
          item.dataType === 'topic' && item.dataID === topicID
        );
        return {
          dataID: topicID,
          dataText: topicData ? topicData.dataText : `Topic ${topicID}`,
          dataIndex: topicData ? topicData.dataIndex : 999999, // Default high number for topics without dataIndex
          questionCount: questionsGroupedByTopic[topicID].length,
          questions: questionsGroupedByTopic[topicID]
        };
      })
      .sort((a, b) => parseInt(a.dataIndex) - parseInt(b.dataIndex));
  }, [courseDesignState]);

  // Get questions for the selected topic
  const questionsForSelectedTopic = useMemo(() => {
    if (!selectedTopicDataID) return [];
    
    return courseDesignState
      .filter(item => 
        item.dataType === 'question' && 
        item.questionCourseDate === selectedTopicDataID
      )
      .sort((a, b) => parseInt(a.dataIndex) - parseInt(b.dataIndex));
  }, [courseDesignState, selectedTopicDataID]);

  // Handle topic selection
  const handleTopicSelect = (topicDataID) => {
    setSelectedTopicDataID(topicDataID);
    
    // Automatically select all questions for the new topic
    const questionsForTopic = courseDesignState
      .filter(item => 
        item.dataType === 'question' && 
        item.questionCourseDate === topicDataID
      );
    const allQuestionIDs = questionsForTopic.map(q => q.dataID);
    setSelectedQuestions(new Set(allQuestionIDs));
  };

  // Handle individual question selection
  const handleQuestionToggle = (questionID) => {
    const newSelection = new Set(selectedQuestions);
    if (newSelection.has(questionID)) {
      newSelection.delete(questionID);
    } else {
      newSelection.add(questionID);
    }
    setSelectedQuestions(newSelection);
  };

  // Handle select all for current topic
  const handleSelectAllTopicQuestions = () => {
    const allTopicQuestionIDs = questionsForSelectedTopic.map(q => q.dataID);
    setSelectedQuestions(new Set(allTopicQuestionIDs));
  };

  // Handle deselect all
  const handleDeselectAll = () => {
    setSelectedQuestions(new Set());
  };

  // Get dependencies for selected questions
  const getDependencies = (questionIDs) => {
    const dependencies = new Set();
    
    const findDependenciesRecursive = (qID) => {
      const question = courseDesignState.find(item => 
        item.dataID === qID && item.dataType === 'question'
      );
      
      if (!question || !question.questionLogic) return;
      
      // Extract referenced questions from logic
      const answerRegex = /\(([^,()]+),\s*(\d+)\)/g;
      let match;
      while ((match = answerRegex.exec(question.questionLogic)) !== null) {
        const referencedQuestionID = match[1].trim();
        if (!dependencies.has(referencedQuestionID) && !questionIDs.includes(referencedQuestionID)) {
          dependencies.add(referencedQuestionID);
          findDependenciesRecursive(referencedQuestionID);
        }
      }
    };

    questionIDs.forEach(qID => findDependenciesRecursive(qID));
    return Array.from(dependencies);
  };

  // Get the final list of questions to share (selected + dependencies)
  const getQuestionsToShare = () => {
    const selectedArray = Array.from(selectedQuestions);
    const dependencies = getDependencies(selectedArray);
    const allQuestionIDs = [...selectedArray, ...dependencies];
    
    return allQuestionIDs.map(qID => 
      courseDesignState.find(item => item.dataID === qID && item.dataType === 'question')
    ).filter(Boolean);
  };

  // Handle sharing questions
  const handleShareQuestions = async () => {
    if (selectedQuestions.size === 0) {
      setMessageModalConfig({
        title: 'No Questions Selected',
        message: 'Please select at least one question to share.',
        type: 'error'
      });
      setShowMessageModal(true);
      return;
    }

    // Get the selected topic name for default module name
    const selectedTopic = topicsWithQuestions.find(t => t.dataID === selectedTopicDataID);
    const topicName = selectedTopic ? selectedTopic.dataText : 'Shared Questions';
    
    // Set default name and show modal
    setDefaultModuleName(topicName);
    setShowPromptModal(true);
  };

  // Handle modal confirmation
  const handleModalConfirm = async (questionModule) => {
    setShowPromptModal(false);
    setIsSharing(true);
    
    try {
      // Get course metadata
      const courseTitle = courseDesignState.find(item => item.dataType === 'courseTitle')?.dataText || '';
      const instructorName = courseDesignState.find(item => item.dataType === 'instructorName')?.dataText || '';
      
      // Get questions to share (including dependencies)
      const questionsToShare = getQuestionsToShare();
      
      // Add course metadata to each question
      const questionsWithMetadata = questionsToShare.map(question => ({
        ...question,
        className: courseTitle
      }));

      // Check for duplicates first
      const duplicateResponse = await axios.post(`${apiUrl}/api/sharing/check-duplicates`, {
        questionModule,
        sharerName: instructorName,
        className: courseTitle
      });

      if (duplicateResponse.data.hasDuplicates) {
        // Store the data for potential sharing and show duplicate resolution modal
        setDuplicateInfo({
          questionModule,
          existingCount: duplicateResponse.data.existingCount,
          questionsToShare: questionsWithMetadata,
          courseTitle,
          instructorName
        });
        setShowDuplicateModal(true);
        setIsSharing(false);
        return;
      }

      // No duplicates, proceed with normal sharing
      await shareQuestions(questionsWithMetadata, questionModule, instructorName);
      
    } catch (error) {
      console.error('Error in sharing process:', error);
      
      // Show error message in modal
      setMessageModalConfig({
        title: 'Sharing Failed',
        message: 'Error sharing questions. Please try again.',
        type: 'error'
      });
      setShowMessageModal(true);
      setIsSharing(false);
    }
  };

  // Function to actually share questions (used by both normal and replace flows)
  const shareQuestions = async (questionsWithMetadata, questionModule, instructorName, isReplace = false) => {
    const endpoint = isReplace ? '/api/sharing/replace-questions' : '/api/sharing/share-questions';
    
    const response = await axios.post(`${apiUrl}${endpoint}`, {
      selectedQuestions: questionsWithMetadata,
      questionModule,
      sharerName: instructorName,
      courseID
    });

    // Show success message in modal
    const action = isReplace ? 'replaced and shared' : 'shared';
    setMessageModalConfig({
      title: 'Questions Shared Successfully',
      message: `Successfully ${action} ${response.data.questionCount} questions to the library`,
      type: 'success'
    });
    setShowMessageModal(true);
    
    // Clear selections after successful sharing
    setSelectedQuestions(new Set());
    setIsSharing(false);
  };

  // Handle modal cancellation
  const handleModalCancel = () => {
    setShowPromptModal(false);
  };

  // Handle message modal close
  const handleMessageModalClose = () => {
    setShowMessageModal(false);
  };

  // Handle duplicate resolution - replace existing
  const handleDuplicateReplace = async () => {
    setShowDuplicateModal(false);
    setIsSharing(true);
    
    try {
      await shareQuestions(
        duplicateInfo.questionsToShare, 
        duplicateInfo.questionModule, 
        duplicateInfo.instructorName, 
        true // isReplace = true
      );
    } catch (error) {
      console.error('Error replacing questions:', error);
      setMessageModalConfig({
        title: 'Replace Failed',
        message: 'Error replacing questions. Please try again.',
        type: 'error'
      });
      setShowMessageModal(true);
      setIsSharing(false);
    }
  };

  // Handle duplicate resolution - choose new name
  const handleDuplicateRename = () => {
    setShowDuplicateModal(false);
    // Show the prompt modal again to get a new name
    setShowPromptModal(true);
  };

  // Handle duplicate resolution - cancel
  const handleDuplicateCancel = () => {
    setShowDuplicateModal(false);
    setIsSharing(false);
  };  return (
    <div style={{ 
      display: 'flex', 
      height: '100vh', 
      fontFamily: 'var(--font-family)',
      backgroundColor: 'var(--background-color)'
    }}>
      {/* Left Sidebar - Topics */}
      <div style={{
        width: '300px',
        backgroundColor: 'white',
        borderRight: '1px solid var(--border-color)',
        padding: '20px',
        overflowY: 'auto',
        boxShadow: 'var(--standard-box-shadow)'
      }}>
        <p style={{
          fontSize: '14px',
          color: 'var(--text-color-muted)',
          marginBottom: '20px',
          lineHeight: '1.4'
        }}>
          After you create questions for days of your course, you can share blocks of questions with other users. Other users will then be able to import these blocks into their own courses, and edit them as they like. To start sharing, pick a day of the course below.
        </p>

        {topicsWithQuestions.length === 0 ? (
          <p style={{ 
            color: 'var(--text-color-muted)',
            fontStyle: 'italic',
            textAlign: 'center',
            marginTop: '40px'
          }}>
            No topics with questions found.
          </p>
        ) : (
          <div>
            {topicsWithQuestions.map(topic => (
              <div
                key={topic.dataID}
                onClick={() => handleTopicSelect(topic.dataID)}
                style={{
                  padding: '10px 12px', // Reduced vertical padding for compactness
                  marginBottom: '4px', // Reduced from 8px to 4px for less white space
                  border: selectedTopicDataID === topic.dataID 
                    ? '2px solid var(--color1)' 
                    : '1px solid var(--border-color)',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  backgroundColor: selectedTopicDataID === topic.dataID 
                    ? 'var(--color1-light)' 
                    : 'white',
                  transition: 'all 0.2s ease',
                  boxShadow: 'var(--standard-box-shadow)' // Always show box shadow to indicate clickability
                }}
                onMouseEnter={(e) => {
                  if (selectedTopicDataID !== topic.dataID) {
                    e.currentTarget.style.backgroundColor = 'var(--hover-color)';
                  }
                }}
                onMouseLeave={(e) => {
                  if (selectedTopicDataID !== topic.dataID) {
                    e.currentTarget.style.backgroundColor = 'white';
                  }
                }}
              >
                <div style={{
                  fontWeight: selectedTopicDataID === topic.dataID ? '600' : '500',
                  fontSize: '14px',
                  color: selectedTopicDataID === topic.dataID 
                    ? 'var(--color1)' 
                    : 'var(--text-color)'
                  // Removed marginBottom since we're removing the question count
                }}>
                  {topic.dataText}
                </div>
                {/* Removed the question count display */}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Main Content Area */}
      <div style={{ 
        flex: 1, 
        display: 'flex', 
        flexDirection: 'column',
        paddingLeft: '20px' // Add space between sidebar and main content
      }}>
        {!selectedTopicDataID ? (
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            height: '100%',
            color: 'var(--text-color-muted)',
            fontSize: '16px'
          }}>
            Select a class day to view its questions and dependencies
          </div>
        ) : (
          <>
            {/* Header with controls */}
            <div style={{
              padding: '20px',
              borderBottom: '1px solid var(--border-color)',
              backgroundColor: 'white',
              boxShadow: 'var(--standard-box-shadow)'
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div>
                  <h2 style={{
                    margin: '0 0 8px 0',
                    color: 'var(--color1)',
                    fontSize: '20px',
                    fontWeight: '600'
                  }}>
                    {topicsWithQuestions.find(t => t.dataID === selectedTopicDataID)?.dataText}
                  </h2>
                  <p style={{
                    margin: 0,
                    color: 'var(--text-color-muted)',
                    fontSize: '14px'
                  }}>
                    {selectedQuestions.size} of {questionsForSelectedTopic.length} questions selected
                    {selectedQuestions.size > 0 && 
                      ` (+${getDependencies(Array.from(selectedQuestions)).length} dependencies)`
                    }
                  </p>
                </div>
                
                <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
                  <button
                    onClick={handleSelectAllTopicQuestions}
                    style={{
                      padding: '8px 16px',
                      backgroundColor: 'var(--color1)',
                      color: 'white',
                      border: 'none',
                      borderRadius: '4px',
                      cursor: 'pointer',
                      fontSize: '14px',
                      fontWeight: '500',
                      transition: 'background-color 0.2s ease'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = 'var(--color2)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = 'var(--color1)';
                    }}
                  >
                    Select All
                  </button>
                  
                  <button
                    onClick={handleDeselectAll}
                    style={{
                      padding: '8px 16px',
                      backgroundColor: 'var(--color1)',
                      color: 'white',
                      border: 'none',
                      borderRadius: '4px',
                      cursor: 'pointer',
                      fontSize: '14px',
                      fontWeight: '500',
                      transition: 'background-color 0.2s ease'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = 'var(--color2)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = 'var(--color1)';
                    }}
                  >
                    Deselect All
                  </button>

                  <button
                    disabled={selectedQuestions.size === 0 || isSharing}
                    style={{
                      padding: '8px 16px',
                      backgroundColor: selectedQuestions.size > 0 && !isSharing 
                        ? 'var(--color1)' 
                        : 'var(--disabled-color)',
                      color: 'white',
                      border: 'none',
                      borderRadius: '4px',
                      cursor: selectedQuestions.size > 0 && !isSharing ? 'pointer' : 'not-allowed',
                      fontSize: '14px',
                      fontWeight: '500',
                      transition: 'background-color 0.2s ease'
                    }}
                    onMouseEnter={(e) => {
                      if (selectedQuestions.size > 0 && !isSharing) {
                        e.currentTarget.style.backgroundColor = 'var(--color2)';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (selectedQuestions.size > 0 && !isSharing) {
                        e.currentTarget.style.backgroundColor = 'var(--color1)';
                      }
                    }}
                    onClick={handleShareQuestions}
                  >
                    {isSharing 
                      ? 'Sharing...' 
                      : `Share Selected (${getQuestionsToShare().length})`
                    }
                  </button>
                </div>
              </div>
            </div>

            {/* React Flow Visualization */}
            <div style={{ flex: 1, backgroundColor: '#f8f9fa' }}>
              <QuestionLogicRepresentation
                courseDesignState={courseDesignState}
                filterByDate={selectedTopicDataID}
                height="100%"
                direction="TB"
                showComplexityIndicators={true}
                enableMiniMap={true}
                onQuestionSelect={(question) => {
                  if (question && question.dataID) {
                    handleQuestionToggle(question.dataID);
                  }
                }}
                selectedQuestions={selectedQuestions.size > 0 ? Array.from(selectedQuestions) : null}
              />
            </div>
          </>
        )}
      </div>

      {/* Prompt Modal */}
      <PromptModal
        isOpen={showPromptModal}
        title="Please provide a name for the shared questions"
        defaultValue={defaultModuleName}
        onConfirm={handleModalConfirm}
        onCancel={handleModalCancel}
      />

      {/* Duplicate Resolution Modal */}
      <DuplicateResolutionModal
        isOpen={showDuplicateModal}
        questionModule={duplicateInfo.questionModule}
        existingCount={duplicateInfo.existingCount}
        onReplace={handleDuplicateReplace}
        onRename={handleDuplicateRename}
        onCancel={handleDuplicateCancel}
      />

      {/* Message Modal */}
      <MessageModal
        isOpen={showMessageModal}
        title={messageModalConfig.title}
        message={messageModalConfig.message}
        type={messageModalConfig.type}
        onClose={handleMessageModalClose}
      />
    </div>
  );
};

export default SharingMaster;
